import api from './client';
import type {
  UserCreate,
  UserUpdate,
  UserResponse,
  ListParams,
} from './types';

export type MemberDocument = {
  id: string;
  name: string;
  url: string;
  content_type?: string;
  size?: number;
  uploaded_at?: string;
};

export const usersApi = {
  /**
   * Get current authenticated user
   * Requires Firebase UID to be passed
   * @param userId - Firebase UID of the current user
   * @returns Current user data
   */
  me: async (userId: string): Promise<UserResponse> => {
    const { data } = await api.get(`/users/${userId}`);
    return data;
  },

  /**
   * List all users, optionally filtered by organization
   * @param params - Query parameters (org_id, limit)
   */
  list: async (params?: ListParams & { org_id?: string }): Promise<UserResponse[]> => {
    const { data } = await api.get('/users', { params });
    return data;
  },

  /**
   * Get a specific user by ID
   * @param userId - User ID
   */
  get: async (userId: string): Promise<UserResponse> => {
    const { data } = await api.get(`/users/${userId}`);
    return data;
  },

  /**
   * Create a new user
   * @param user - User data
   */
  create: async (user: UserCreate): Promise<UserResponse> => {
    const { data } = await api.post('/users', user);
    return data;
  },

  /**
   * Update an existing user
   * @param userId - User ID
   * @param updates - Fields to update
   */
  update: async (userId: string, updates: UserUpdate): Promise<UserResponse> => {
    const { data } = await api.patch(`/users/${userId}`, updates);
    return data;
  },

  /**
   * Delete a user
   * @param userId - User ID
   */
  delete: async (userId: string): Promise<void> => {
    await api.delete(`/users/${userId}`);
  },

  /**
   * Upload profile picture for a user
   * @param userId - User ID
   * @param file - Image file to upload
   * @returns Object with photo_url and message
   */
  /** Documents of a member (stagiaire convention…), managers only. `url` is signed for 1 h. */
  listDocuments: async (userId: string): Promise<MemberDocument[]> => {
    const { data } = await api.get(`/users/${userId}/documents`);
    return data;
  },

  uploadDocument: async (userId: string, file: File): Promise<MemberDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post(`/users/${userId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  deleteDocument: async (userId: string, docId: string): Promise<void> => {
    await api.delete(`/users/${userId}/documents/${docId}`);
  },

  uploadProfilePicture: async (userId: string, file: File): Promise<{ photo_url: string; message: string }> => {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await api.post(`/users/${userId}/profile-picture`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },

  /**
   * Look up a user by email within the current user's org.
   * Returns minimal profile info or null if not found.
   * Uses validateStatus to avoid triggering the global 401 logout handler.
   */
  lookup: async (email: string): Promise<{ id: string; display_name: string; email: string; photo_url?: string } | null> => {
    try {
      const { data, status } = await api.get('/users/lookup', {
        params: { email },
        validateStatus: (s) => s < 500,
      });
      return status === 200 ? data : null;
    } catch {
      return null;
    }
  },

  /**
   * Upload signature image for a user
   * @param userId - User ID
   * @param file - Image file to upload
   * @returns Object with signature_url and message
   */
  sendPasswordReset: async (userId: string): Promise<{ message: string }> => {
    const { data } = await api.post(`/users/${userId}/send-password-reset`);
    return data;
  },

  updateEmail: async (userId: string, newEmail: string): Promise<{ message: string }> => {
    const { data } = await api.post(`/users/${userId}/update-email`, { new_email: newEmail });
    return data;
  },

  uploadSignature: async (userId: string, file: File): Promise<{ signature_url: string; message: string }> => {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await api.post(`/users/${userId}/signature`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },
};
